import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { FIELD, inr } from "./_shared";
import ReceiptField from "./ReceiptField";

const INSTALL_EXPENSE_CATS = ["food", "travel", "lodging", "materials", "labour", "misc"];

/** Installation Expenses — Installation team logs on-site spends with receipt photo (required).
 *  Cost visible: super_admin, accounts, admin. Installation team sees rows but not ₹. */
export default function InstallationExpensesCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts", "admin", "installation"].includes(role);
  const canWrite = ["super_admin", "installation"].includes(role);
  const showCost = ["super_admin", "accounts", "admin"].includes(role);
  const receiptRequired = role === "installation";

  const [state, setState] = useState({ rows: [], total: 0, by_category: [] });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "food", amount: "", notes: "", receipt: null,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/costing/project/${p.id}/installation-expenses`); setState(data); }
    catch { /* noop */ }
  };
  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, []);
  if (!canView) return null;

  const submit = async () => {
    if (!form.date || !form.amount) { toast.error("Date and amount are required"); return; }
    if (receiptRequired && !form.receipt) { toast.error("Attach a receipt photo"); return; }
    setSaving(true);
    try {
      await api.post(`/costing/project/${p.id}/installation-expenses`, {
        date: form.date, category: form.category,
        amount: Number(form.amount), notes: form.notes || null,
        receipt_file_id: form.receipt?.file_id || null,
      });
      toast.success("Installation expense added");
      setForm({ date: form.date, category: "food", amount: "", notes: "", receipt: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/costing/project/${p.id}/installation-expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hk-card p-4" data-testid="installation-expenses-card">
      <div className="hk-tiny-label mb-2">Installation expenses</div>
      {canWrite && (
        <div className="border border-slate-200 rounded-sm p-2 mb-3 space-y-1">
          <div className="grid grid-cols-3 gap-1">
            <input className={FIELD} type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="ie-date" />
            <select className={FIELD} value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="ie-category">
              {INSTALL_EXPENSE_CATS.map((c) => (
                <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
            <input className={FIELD} type="number" step="0.01" placeholder="Amount (₹)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="ie-amount" />
          </div>
          <input className={FIELD} placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="ie-notes" />
          <ReceiptField p={p} receipt={form.receipt}
            onChange={(r) => setForm({ ...form, receipt: r })}
            required={receiptRequired} testid="ie-receipt" />
          <button type="button" onClick={submit} disabled={saving}
            data-testid="ie-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Add expense
          </button>
        </div>
      )}
      {state.rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="ie-empty">No installation expenses yet.</div>
      ) : (
        <>
          <ul className="space-y-1.5" data-testid="ie-list">
            {state.rows.map((r) => {
              const canDelete = role === "super_admin"
                || (role === "installation" && r.user_id === user?.id);
              return (
                <li key={r.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 capitalize">
                        {r.category} {showCost ? <>· {inr(r.amount)}</> : null}
                      </div>
                      <div className="text-slate-600">{r.date} · By {r.user_name}</div>
                      {r.notes && <div className="text-slate-500 mt-0.5">{r.notes}</div>}
                      {r.receipt && (
                        <a href={`${api.defaults.baseURL}/costing/project/${p.id}/receipts/${r.receipt.file_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-orange-700 hover:text-orange-900 underline mt-0.5 inline-block"
                          data-testid={`ie-receipt-${r.id}`}>
                          📎 {r.receipt.name}
                        </a>
                      )}
                    </div>
                    {canDelete && (
                      <button type="button" onClick={() => del(r.id)}
                        data-testid={`ie-del-${r.id}`}
                        className="text-red-600 hover:text-red-800 shrink-0" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {showCost && (
            <>
              <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs"
                data-testid="ie-totals">
                <span className="font-bold text-slate-700">Entries: {state.rows.length}</span>
                <span className="font-bold text-slate-900">Total: {inr(state.total)}</span>
              </div>
              {state.by_category.length > 1 && (
                <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5 text-[10px]"
                  data-testid="ie-by-category">
                  <div className="hk-tiny-label mb-0.5">By category</div>
                  {state.by_category.map((s) => (
                    <div key={s.category} className="flex justify-between text-slate-600 capitalize">
                      <span>{s.category}</span>
                      <span>{inr(s.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
